import { Loader2 } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import InfoIcon from '@/assets/icons/information.svg';
import { Button } from '@/components/ui/button';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { FormDatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { FormRadioGroup } from '@/components/ui/radio-group';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useBudgetType } from '@/hooks/use-budget-type';
import { useIndeedEndDateTypeOptions } from '@/hooks/use-indeed-end-date-type-options';
import { INDEED, JOVEO, ZIPRECRUITER } from '@/types/channels';
import { getChannelName } from '@/utils/channel-names';
import { addDays, durationInDays } from '@/utils/date-util';

import { BudgetType } from '../types';
import {
  getChannelConstraints,
  numericInputSetValueAs,
} from '../utils/channel-utils';
import { BudgetAllocation } from './budget-allocation';
import { BudgetCalculationSection } from './budget-calculation-section';
import { useChannelFormContext } from './channel-form-context';
import { Conflicts } from './conflicts';
import { ManageOpenings } from './manage-openings';
import { VonqPostingIssues } from './vonq-posting-issues';
import { ZipRecruiterBudget } from './zip-recruiter-budget';

interface ChannelFormProps {
  /** Form-state key (channel slug or, for Vonq, product ID). */
  channelKey: string;
}

export function ChannelForm({ channelKey }: ChannelFormProps) {
  const intl = useIntl();
  const { budgetTypeOptions, getJobPostingBudgetLabel } = useBudgetType();
  const indeedEndDateTypeOptions = useIndeedEndDateTypeOptions();
  const {
    isPending,
    validateChannelBudget,
    removeChannelFromPosting,
    cancelChannelChanges,
    validateChannelChanges,
    openingsWithConflicts,
    handleBudgetTypeChange,
    clearConflicts,
    removeConflictingOpening,
    removeOpeningWithIssues,
    conflicts,
    vonqPostingIssues,
  } = useChannelFormContext();
  const { hookForm, selectedChannelKeys } = useJobPostingContext();
  const {
    register,
    control,
    setValue,
    formState: { errors, dirtyFields },
    clearErrors,
  } = hookForm;

  const channel = useWatch({
    control: hookForm.control,
    name: `channels.${channelKey}`,
  });

  const {
    channelBudget,
    totalChannelBudget,
    openingBudgets,
    budgetType,
    startsAt,
    endAt,
    slug: channelSlug,
    name: channelName,
    displayTitle,
    vonqDetails,
    isFree,
  } = channel;

  const duration = endAt && startsAt ? durationInDays(startsAt, endAt) : null;

  const {
    defaults: channelDefaults,
    isBudgetReadOnly,
    isEndDateReadOnly,
    minDuration,
  } = getChannelConstraints(channelSlug, vonqDetails);
  const isStartDateReadOnly = !!vonqDetails;

  const isChannelDirty = dirtyFields?.channels?.[channelKey];
  // Reset the validated status when the channel is dirty so that the channel can be validated again
  useEffect(() => {
    if (isChannelDirty) setValue(`channels.${channelKey}.isValidated`, false);
  }, [channelKey, isChannelDirty, setValue]);

  // Calculate minimum end date based on start date and channel constraints
  const minEndDate = useMemo(() => {
    if (!startsAt) return undefined;

    const startDate = new Date(startsAt);
    return addDays(startDate, minDuration);
  }, [startsAt, minDuration]);

  const handleDateChange = () => {
    validateChannelBudget();
    clearConflicts(); // Clear conflicts when date changes
    clearErrors(`channels.${channelKey}.endAt`);
  };

  // Handle start date change and auto-update end date based on constraints
  const handleStartDateChange = (newStartDate: string) => {
    const startDate = new Date(newStartDate);
    const calculatedEndDate = addDays(startDate, minDuration).toISOString();

    // Case 1: Fixed duration - always set the end date
    if (channelDefaults.duration) {
      setValue(`channels.${channelKey}.endAt`, calculatedEndDate);
      return;
    }

    // Case 2: Minimum duration - only set if not set or if it violates minimum
    if (channelDefaults.minDuration || minDuration) {
      const currentEndDate = endAt ? new Date(endAt) : null;
      if (!currentEndDate || currentEndDate < new Date(calculatedEndDate)) {
        setValue(`channels.${channelKey}.endAt`, calculatedEndDate);
      }
    }
    handleDateChange();
  };

  // Check if all openings selected in a channel have conflicts
  const allOpeningsHaveConflicts = useMemo(
    () =>
      Object.keys(openingBudgets).every(
        id => openingsWithConflicts && openingsWithConflicts.includes(id),
      ),
    [openingBudgets, openingsWithConflicts],
  );

  // Check if all openings selected in a channel have posting issues
  const allOpeningsHaveVonqPostingIssues = useMemo(
    () =>
      Object.keys(openingBudgets).every(id =>
        vonqPostingIssues.some(issue => issue.openingId === id),
      ),
    [openingBudgets, vonqPostingIssues],
  );

  return (
    <div className="border-fx-neutral-200 mb-2 w-full rounded-lg border bg-white p-4 shadow-sm">
      <div className="flex flex-1 flex-col gap-4">
        <ChannelIcon
          channelSlug={channelSlug}
          channelName={channelName}
          displayTitle={displayTitle}
          variant="large"
          className="mx-auto"
          logoSquareUrl={vonqDetails?.logoSquareUrl}
          logoRectangleUrl={vonqDetails?.logoRectangleUrl}
        />

        {/* Budget type for Joveo */}
        {channelSlug === JOVEO && (
          <FormRadioGroup
            control={control}
            name={`channels.${channelKey}.budgetType`}
            options={budgetTypeOptions}
            radioGroupClassName="justify-between"
            onChange={value => handleBudgetTypeChange(value as BudgetType)}
          />
        )}

        {/* End date type for Indeed */}
        {channelSlug === INDEED && (
          <FormRadioGroup
            control={control}
            name={`channels.${channelKey}.endAtType`}
            options={indeedEndDateTypeOptions}
          />
        )}

        {channelSlug === ZIPRECRUITER && (
          <ZipRecruiterBudget channelKey={channelKey} />
        )}

        {!isFree && channelSlug !== ZIPRECRUITER && (
          <Input
            isNumber
            {...register(`channels.${channelKey}.channelBudget`, {
              setValueAs: numericInputSetValueAs,
              onChange: validateChannelBudget,
            })}
            label={getJobPostingBudgetLabel(budgetType)}
            placeholderLabel="$"
            placeholder={intl.formatMessage({
              defaultMessage: 'Enter total spend for the channel',
              description:
                'Placeholder for the total spend input for the channel',
            })}
            readOnly={isBudgetReadOnly}
            error={errors?.channels?.[channelKey]?.channelBudget?.message}
            required
          />
        )}

        {!isFree && (
          <BudgetAllocation
            channelKey={channelKey}
            isReadOnly={isBudgetReadOnly}
          />
        )}

        <div className="flex gap-6">
          {/* Campaign start date */}
          <div className="flex-1">
            <FormDatePicker
              isStartOfDay
              control={control}
              name={`channels.${channelKey}.startsAt`}
              label={intl.formatMessage({
                defaultMessage: 'Start date',
                description:
                  'Form label for when the job board campaign can begin',
              })}
              placeholder={intl.formatMessage({
                defaultMessage: 'Enter start date',
                description: 'Placeholder for the start date input',
              })}
              onChange={handleStartDateChange}
              required
              readOnly={isStartDateReadOnly}
            />
          </div>

          {/* Campaign end date */}
          <div className="relative flex-1">
            <FormDatePicker
              isEndOfDay
              control={control}
              name={`channels.${channelKey}.endAt`}
              label={intl.formatMessage({
                defaultMessage: 'End date',
                description:
                  'Form label for when the job board campaign should stop',
              })}
              placeholder={intl.formatMessage({
                defaultMessage: 'Enter end date',
                description: 'Placeholder for the end date input',
              })}
              error={errors?.channels?.[channelKey]?.endAt?.message}
              readOnly={isEndDateReadOnly}
              minDate={minEndDate}
              onChange={handleDateChange}
              required
            />
            {/* Information icon for fixed duration / minimum duration */}
            {(channelDefaults.duration || channelDefaults.minDuration) && (
              <div className="absolute top-1 left-18">
                <Tooltip
                  className="w-72"
                  content={
                    channelDefaults.duration
                      ? intl.formatMessage(
                          {
                            defaultMessage:
                              '{channelName} campaigns have a fixed duration of {duration} days. Your end date is set automatically based on the start date.',
                            description:
                              'Tooltip: fixed campaign length for a job board when duration is set',
                          },
                          {
                            channelName: getChannelName(channelSlug),
                            duration: channelDefaults.duration ?? 0,
                          },
                        )
                      : intl.formatMessage(
                          {
                            defaultMessage:
                              '{channelName} requires a minimum duration of {duration} days. Your end date is set automatically based on the start date.',
                            description:
                              'Tooltip: minimum number of days required for a channel campaign',
                          },
                          {
                            channelName: getChannelName(channelSlug),
                            duration: channelDefaults.minDuration ?? 0,
                          },
                        )
                  }
                >
                  <img src={InfoIcon} alt="Info" />
                </Tooltip>
              </div>
            )}
          </div>
        </div>
        {duration && (
          <Typography variant="body1" className="!text-fx-neutral-600 -mt-4">
            <FormattedMessage
              defaultMessage="Duration: {duration} {duration, plural, one {day} other {days}}"
              values={{
                duration: duration ?? '-',
              }}
              description="Summary under dates: {duration} uses ICU plural for day(s)"
            />
          </Typography>
        )}

        <ManageOpenings channelKey={channelKey} isFree={isFree} />

        <Conflicts
          allOpeningsHaveConflicts={allOpeningsHaveConflicts}
          openingsWithConflicts={openingsWithConflicts}
          removeConflictingOpening={removeConflictingOpening}
          conflicts={conflicts}
        />

        <VonqPostingIssues
          issues={vonqPostingIssues}
          removeOpening={removeOpeningWithIssues}
          allOpeningsHaveVonqPostingIssues={allOpeningsHaveVonqPostingIssues}
        />

        <BudgetCalculationSection
          channelKey={channelKey}
          unitBudget={channelBudget}
          totalBudget={totalChannelBudget}
          className="border-fx-neutral-200 bg-fx-neutral-50 rounded-lg border p-4"
          isFree={isFree}
        />

        <div className="flex justify-between gap-6">
          {selectedChannelKeys.length > 1 && (
            <Button
              type="button"
              variant="ghost"
              className="!text-fx-neutral-600"
              onClick={removeChannelFromPosting}
            >
              <FormattedMessage
                defaultMessage="Remove from posting"
                description="Remove the channel from the job posting"
              />
            </Button>
          )}

          <div className="ml-auto flex gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={cancelChannelChanges}
            >
              <FormattedMessage
                defaultMessage="Cancel"
                description="Cancel the channel changes"
              />
            </Button>
            <Button type="button" onClick={() => void validateChannelChanges()}>
              {isPending && <Loader2 className="animate-spin" />}
              <FormattedMessage
                defaultMessage="Save"
                description="Save the channel changes"
              />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
